import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  useSims,
  useUpdateSimStatus,
  useBlacklistSim,
} from "@/hooks/use-sims";
import {
  IconPlus,
  IconRefresh,
  IconSearch,
  IconDeviceSim,
  IconActivity,
  IconTrendingUp,
  IconDots,
} from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import type { SIMParams } from "@/types/api";
import { formatDistanceToNow } from "date-fns";

import { AppShell } from "@/components/layout/app-shell";
import { ProtectedRoute } from "@/components/layout/protected-route";
import { SimMerchantAssignDialog } from "@/components/sims/sim-merchant-assign-dialog";

export const Route = createFileRoute("/sims/")({
  component: SimsPage,
});
function SimsPage() {
  return (
    <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
      <AppShell>
        <SimsPageContent />
      </AppShell>
    </ProtectedRoute>
  );
}


function SimsPageContent() {
  const [filters, setFilters] = useState<SIMParams>({
    page: 1,
    limit: 50,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [operatorFilter, setOperatorFilter] = useState<string>("all");
  const [blacklistDialog, setBlacklistDialog] = useState<{
    open: boolean;
    simId: string;
    phoneNumber: string;
  }>({ open: false, simId: "", phoneNumber: "" });

  const [merchantAssignDialog, setMerchantAssignDialog] = useState<{
    open: boolean;
    simId: string;
    phoneNumber: string;
  }>({ open: false, simId: "", phoneNumber: "" });

  const { data, isLoading, refetch } = useSims(filters);
  const blacklistSim = useBlacklistSim();
  const updateSimStatus = useUpdateSimStatus();

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    // Client-side search for phone numbers
  };

  const handleStatusFilter = (value: string) => {
    setStatusFilter(value);
    setFilters((prev) => ({
      ...prev,
      status: value === "all" ? undefined : (value as any),
      page: 1,
    }));
  };

  const handleOperatorFilter = (value: string) => {
    setOperatorFilter(value);
    setFilters((prev) => ({
      ...prev,
      operator: value === "all" ? undefined : (value as any),
      page: 1,
    }));
  };

  const handleUpdateStatus = async (
    simId: string,
    status: "active" | "inactive" | "suspended",
    phoneNumber: string,
  ) => {
    try {
      await updateSimStatus.mutateAsync({
        simId,
        status,
        reason: `Status changed to ${status} via admin dashboard`,
      });
      toast.success(`SIM ${phoneNumber} status updated to ${status}`);
    } catch (error: any) {
      toast.error(error.message || "Failed to update SIM status");
    }
  };

  const handleBlacklist = async () => {
    try {
      await blacklistSim.mutateAsync({
        simId: blacklistDialog.simId,
        reason: "Blacklisted via admin dashboard",
      });
      toast.success(`SIM ${blacklistDialog.phoneNumber} blacklisted successfully`);
      setBlacklistDialog({ open: false, simId: "", phoneNumber: "" });
    } catch (error: any) {
      toast.error(error.message || "Failed to blacklist SIM");
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "active":
        return "default";
      case "inactive":
        return "outline";
      case "suspended":
        return "secondary";
      case "blacklisted":
        return "destructive";
      default:
        return "outline";
    }
  };

  const getOperatorColor = (operator: string) => {
    switch (operator) {
      case "bKash":
        return "text-pink-500";
      case "Nagad":
        return "text-orange-500";
      case "Rocket":
        return "text-purple-500";
      default:
        return "text-gray-500";
    }
  };

  // Client-side filtering for search
  const filteredData = (data || []).filter((sim: any) =>
    searchTerm
      ? sim.phoneNumber.includes(searchTerm) ||
        sim?.agent?.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sim?.agent?.serialNumber?.toLowerCase().includes(searchTerm.toLowerCase())
      : true,
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">SIM Management</h1>
          <p className="text-muted-foreground mt-1">
            Manage SIM cards and merchant assignments
          </p>
        </div>
        <Button>
          <IconPlus className="h-4 w-4 mr-2" />
          Add SIM
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by phone number, agent name, or serial..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={operatorFilter} onValueChange={handleOperatorFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by operator" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Operators</SelectItem>
            <SelectItem value="bKash">bKash</SelectItem>
            <SelectItem value="Nagad">Nagad</SelectItem>
            <SelectItem value="Rocket">Rocket</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={handleStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="blacklisted">Blacklisted</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <IconRefresh className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Summary */}
      {data && (
        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">
                Total SIMs
              </p>
              <IconDeviceSim className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold mt-2">{data?.pagination?.total || 0}</p>
          </div>
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">
                Active SIMs
              </p>
              <IconActivity className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {data?.data?.filter((s: any) => s.status === "active").length || 0}
            </p>
          </div>
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">
                30-Day Transactions
              </p>
              <IconTrendingUp className="h-4 w-4 text-blue-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {data?.data?.reduce(
                (acc: number, s: any) => acc + (s?.stats?.last30Days?.totalTransactions || 0),
                0,
              ) || 0}
            </p>
          </div>
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">
                Avg Success Rate
              </p>
              <IconTrendingUp className="h-4 w-4 text-purple-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {data && data.length > 0
                ? (
                    data.reduce(
                      (acc: number, s: any) => acc + (s?.stats?.last30Days?.successRate || 0),
                      0,
                    ) / data.length
                  ).toFixed(1)
                : "0.0"}
              %
            </p>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Phone Number</TableHead>
              <TableHead>Operator</TableHead>
              <TableHead>Agent</TableHead>
              <TableHead>Merchants</TableHead>
              <TableHead>30-Day Stats</TableHead>
              <TableHead>Success Rate</TableHead>
              <TableHead>Last Activity</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8">
                  Loading SIMs...
                </TableCell>
              </TableRow>
            ) : !filteredData || filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8">
                  No SIMs found
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((sim: any) => (
                <TableRow key={sim.id}>
                  <TableCell>
                    <div className="font-mono font-medium">
                      {sim.phoneNumber}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={getOperatorColor(sim.operator)}
                    >
                      {sim.operator}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{sim?.agent?.fullName || "Unknown Agent"}</div>
                      <div className="text-sm text-muted-foreground">
                        {sim?.agent?.serialNumber || "N/A"}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {sim.merchants && sim.merchants.length > 0 ? (
                      <div className="space-y-1">
                        {sim.merchants.slice(0, 2).map((merchant: any) => (
                          <Badge key={merchant.id} variant="secondary">
                            {merchant.businessName}
                          </Badge>
                        ))}
                        {sim.merchants.length > 2 && (
                          <Badge variant="outline">
                            +{sim.merchants.length - 2} more
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">
                        No assignments
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div>
                        {sim?.stats?.last30Days?.totalTransactions || 0} transactions
                      </div>
                      <div className="text-muted-foreground">
                        ৳
                        {(sim?.stats?.last30Days?.totalAmount || 0).toLocaleString(
                          "en-BD",
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        (sim?.stats?.last30Days?.successRate || 0) >= 95
                          ? "default"
                          : (sim?.stats?.last30Days?.successRate || 0) >= 85
                            ? "secondary"
                            : "destructive"
                      }
                    >
                      {(sim?.stats?.last30Days?.successRate || 0).toFixed(1)}%
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {sim.lastActivityAt
                      ? formatDistanceToNow(new Date(sim.lastActivityAt), {
                          addSuffix: true,
                        })
                      : "Unknown"}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(sim.status)}>
                      {sim.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <IconDots className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>View Details</DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            setMerchantAssignDialog({
                              open: true,
                              simId: sim.id,
                              phoneNumber: sim.phoneNumber,
                            })
                          }
                        >
                          Assign/Unassign Merchants
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {sim.status !== "active" && (
                          <DropdownMenuItem
                            onClick={() =>
                              handleUpdateStatus(
                                sim.id,
                                "active",
                                sim.phoneNumber,
                              )
                            }
                          >
                            Activate
                          </DropdownMenuItem>
                        )}
                        {sim.status !== "suspended" && (
                          <DropdownMenuItem
                            onClick={() =>
                              handleUpdateStatus(
                                sim.id,
                                "suspended",
                                sim.phoneNumber,
                              )
                            }
                          >
                            Suspend
                          </DropdownMenuItem>
                        )}
                        {sim.status !== "inactive" && (
                          <DropdownMenuItem
                            onClick={() =>
                              handleUpdateStatus(
                                sim.id,
                                "inactive",
                                sim.phoneNumber,
                              )
                            }
                          >
                            Deactivate
                          </DropdownMenuItem>
                        )}
                        {sim.status !== "blacklisted" && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() =>
                                setBlacklistDialog({
                                  open: true,
                                  simId: sim.id,
                                  phoneNumber: sim.phoneNumber,
                                })
                              }
                            >
                              Blacklist
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {data?.pagination?.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {data?.pagination ? (filters.page! - 1) * filters.limit! + 1 : 0} to{" "}
            {data?.pagination
              ? Math.min(
                  filters.page! * filters.limit!,
                  data.pagination.total,
                )
              : 0}{" "}
            of {data?.pagination?.total || 0} SIMs
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={filters.page === 1}
              onClick={() =>
                setFilters((prev) => ({ ...prev, page: prev.page! - 1 }))
              }
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={filters.page === data?.pagination?.totalPages}
              onClick={() =>
                setFilters((prev) => ({ ...prev, page: prev.page! + 1 }))
              }
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Blacklist Confirmation Dialog */}
      <AlertDialog
        open={blacklistDialog.open}
        onOpenChange={(open) =>
          !open &&
          setBlacklistDialog({ open: false, simId: "", phoneNumber: "" })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Blacklist SIM?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to blacklist SIM{" "}
              <span className="font-mono font-semibold">
                {blacklistDialog.phoneNumber}
              </span>
              ? This action will prevent this SIM from processing any
              transactions. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBlacklist}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Blacklist
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Merchant Assignment Dialog */}
      <SimMerchantAssignDialog
        open={merchantAssignDialog.open}
        onOpenChange={(open) =>
          setMerchantAssignDialog({ open, simId: "", phoneNumber: "" })
        }
        simId={merchantAssignDialog.simId}
        simPhoneNumber={merchantAssignDialog.phoneNumber}
      />
    </div>
  );
}
